import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ThemeProvider, useTheme, SectionCard,
  Topbar, LoadingScreen, EmptyState, ProgressBar, UserProfileCard, AnimatedStatCard
} from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

// ── Live countdown timer ──────────────────────────────────────────────────────
function CountdownTimer({ deadline, resolvedAt }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (resolvedAt) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [resolvedAt]);

  if (!deadline) return <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>—</span>;

  const end = new Date(deadline).getTime();
  const ref = resolvedAt ? new Date(resolvedAt).getTime() : now;
  const diff = end - ref;
  const isOverdue = diff < 0;
  const abs = Math.abs(diff);

  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);

  const label = d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m ${s}s` : `${m}m ${s}s`;
  const color = isOverdue ? '#ef4444' : diff < 3600000 ? '#f59e0b' : diff < 86400000 ? '#fbbf24' : '#10b981';

  return (
    <span style={{ fontSize: 12, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
      {isOverdue ? '⚠ +' : '⏱ '}{label}
    </span>
  );
}

// ── Mini bar chart row ────────────────────────────────────────────────────────
function PerfBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
      <div style={{ width: 64, fontSize: 10, color: 'rgba(255,255,255,0.5)', textAlign: 'right', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, height: 7, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', borderRadius: 4, background: color, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ width: 28, fontSize: 11, fontWeight: 700, color, textAlign: 'right', flexShrink: 0 }}>{value}</div>
    </div>
  );
}

// ── Admin performance card ────────────────────────────────────────────────────
function AdminPerfCard({ admin, maxVerified, s }) {
  const resolvePct = admin.total > 0 ? Math.round((admin.verified / admin.total) * 100) : 0;
  return (
    <div style={{ ...s.card, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: admin.admin_type === 'university' ? 'rgba(99,102,241,0.3)' : 'rgba(16,185,129,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, color: '#fff',
          }}>
            {admin.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{admin.name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
              {admin.admin_type === 'university' ? 'University Admin' : admin.department}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: admin.admin_type === 'university' ? 'rgba(99,102,241,0.25)' : 'rgba(16,185,129,0.25)',
            color: admin.admin_type === 'university' ? '#a5b4fc' : '#6ee7b7',
          }}>
            {admin.admin_type?.toUpperCase()}
          </span>
          {admin.negligence_count > 0 && (
            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 20, background: 'rgba(239,68,68,0.2)', color: '#fca5a5' }}>
              ⚠ {admin.negligence_count}
            </span>
          )}
        </div>
      </div>

      {/* Stat pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Verified', val: admin.verified, color: COLORS.blue },
          { label: 'Rejected', val: admin.rejected, color: '#f87171' },
          { label: 'Pending',  val: admin.pending,  color: COLORS.amber },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ textAlign: 'center', padding: '4px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Progress bars */}
      <PerfBar label="Verified"  value={admin.verified} max={Math.max(maxVerified, 1)} color={COLORS.blue} />
      <PerfBar label="Rejected"  value={admin.rejected} max={Math.max(maxVerified, 1)} color="#f87171" />
      <PerfBar label="Pending"   value={admin.pending}  max={Math.max(maxVerified, 1)} color={COLORS.amber} />

      {/* Efficiency bar */}
      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Verification rate</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: resolvePct >= 70 ? COLORS.green : resolvePct >= 40 ? COLORS.amber : '#f87171' }}>{resolvePct}%</span>
        </div>
        <ProgressBar value={resolvePct} color={resolvePct >= 70 ? COLORS.green : resolvePct >= 40 ? COLORS.amber : '#f87171'} showPct={false} />
      </div>
    </div>
  );
}

// ── Authority performance card ────────────────────────────────────────────────
function AuthPerfCard({ auth, maxResolved, s }) {
  const resolvePct = auth.assigned > 0 ? Math.round((auth.resolved / auth.assigned) * 100) : 0;
  return (
    <div style={{ ...s.card, padding: '14px 16px', marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
            background: auth.authority_type === 'university' ? 'rgba(99,102,241,0.3)' : 'rgba(245,158,11,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, fontWeight: 800, color: '#fff',
          }}>
            {auth.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>{auth.name}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>
              {auth.authority_type === 'university' ? 'University Authority' : auth.department}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: auth.authority_type === 'university' ? 'rgba(99,102,241,0.25)' : 'rgba(245,158,11,0.25)',
            color: auth.authority_type === 'university' ? '#a5b4fc' : '#fcd34d',
          }}>
            {auth.authority_type?.toUpperCase()}
          </span>
          {auth.negligence_count > 0 && (
            <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 20, background: 'rgba(239,68,68,0.2)', color: '#fca5a5' }}>
              ⚠ {auth.negligence_count}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        {[
          { label: 'Resolved',  val: auth.resolved,  color: COLORS.green },
          { label: 'Assigned',  val: auth.assigned,  color: COLORS.cyan },
          { label: 'Overdue',   val: auth.overdue,   color: '#f87171' },
          { label: 'Escalated', val: auth.escalated, color: COLORS.amber },
        ].map(({ label, val, color }) => (
          <div key={label} style={{ textAlign: 'center', padding: '4px 10px', borderRadius: 8, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color }}>{val}</div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>{label}</div>
          </div>
        ))}
      </div>

      <PerfBar label="Resolved"  value={auth.resolved}  max={Math.max(maxResolved, 1)} color={COLORS.green} />
      <PerfBar label="Assigned"  value={auth.assigned}  max={Math.max(maxResolved, 1)} color={COLORS.cyan} />
      <PerfBar label="Overdue"   value={auth.overdue}   max={Math.max(maxResolved, 1)} color="#f87171" />

      <div style={{ marginTop: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Resolution rate</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: resolvePct >= 70 ? COLORS.green : resolvePct >= 40 ? COLORS.amber : '#f87171' }}>{resolvePct}%</span>
        </div>
        <ProgressBar value={resolvePct} color={resolvePct >= 70 ? COLORS.green : resolvePct >= 40 ? COLORS.amber : '#f87171'} showPct={false} />
      </div>
    </div>
  );
}

// ── Main dashboard ────────────────────────────────────────────────────────────
function HigherAuthorityDashboardInner() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState({ admins: [], authorities: [] });
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, perfRes, assignedRes] = await Promise.allSettled([
        api.get('/higher-authority/dashboard-stats'),
        api.get('/higher-authority/performance'),
        api.get('/authority/assigned-complaints'),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (perfRes.status === 'fulfilled') setPerf(perfRes.value.data);
      if (assignedRes.status === 'fulfilled') setAssigned(Array.isArray(assignedRes.value.data) ? assignedRes.value.data : []);
      setLastRefresh(new Date());
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 30000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  if (loading) return <LoadingScreen />;

  const maxVerified = perf.admins.length ? Math.max(...perf.admins.map(a => a.verified), 1) : 1;
  const maxResolved = perf.authorities.length ? Math.max(...perf.authorities.map(a => a.resolved), 1) : 1;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} /><div style={s.bgOrb1} /><div style={s.bgOrb2} /><div style={s.bgOrb3} />
      <div style={s.layout}>
        <Sidebar collapsed={!sidebarOpen} onToggle={() => setSidebarOpen(o => !o)}
          mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Higher Authority Dashboard"
            subtitle="Top-level system oversight"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={<button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={fetchAll}>Refresh</button>}
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            <UserProfileCard user={user} />

            {/* System stats */}
            <div style={{ marginBottom: 20 }}>
              {/* Complaints row */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                📋 Complaints Overview
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }}>
                {[
                  { label: 'Total',     value: stats?.total_complaints || 0,    color: COLORS.purple },
                  { label: 'Pending',   value: stats?.pending_complaints || 0,  color: COLORS.amber },
                  { label: 'Resolved',  value: stats?.resolved_complaints || 0, color: COLORS.green },
                  { label: 'Escalated', value: stats?.escalated_complaints || 0,color: COLORS.red },
                ].map(({ label, value, color, icon }, i) => (
                  <AnimatedStatCard key={label} label={label} value={value} color={color} delay={`${0.05 + i * 0.06}s`} />
                ))}
              </div>

              {/* Users row */}
              <div style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
                👥 User Directory
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                {[
                  { label: 'Total Users',   value: stats?.total_users || 0,       color: COLORS.blue },
                  { label: 'Students',      value: stats?.total_students || 0,    color: COLORS.cyan },
                  { label: 'Faculty',       value: stats?.total_faculty || 0,     color: COLORS.violet },
                  { label: 'Admins',        value: stats?.total_admins || 0,      color: COLORS.amber },
                  { label: 'Authorities',   value: stats?.total_authorities || 0, color: COLORS.teal },
                ].map(({ label, value, color, icon }, i) => (
                  <AnimatedStatCard key={label} label={label} value={value} color={color} compact delay={`${0.05 + i * 0.06}s`} />
                ))}
              </div>
            </div>

            {/* Pending reports alert */}
            {(stats?.pending_admin_reports > 0 || stats?.pending_authority_reports > 0) && (
              <div style={{ ...s.card, marginBottom: 20, background: 'rgba(245,158,11,0.1)', border: `1px solid ${COLORS.amber}44`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 12 : 20, alignItems: isMobile ? 'flex-start' : 'center', padding: '14px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 20 }}>📋</span>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.amber }}>Reports Awaiting Review</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>
                      {stats?.pending_admin_reports || 0} admin report{stats?.pending_admin_reports !== 1 ? 's' : ''} &nbsp;·&nbsp;
                      {stats?.pending_authority_reports || 0} authority report{stats?.pending_authority_reports !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginLeft: isMobile ? 0 : 'auto' }}>
                  <button className="ur-btn-hover" style={{ ...s.btnPrimary, fontSize: 12, padding: '6px 12px' }} onClick={() => navigate('/higher-authority/admin-reports')}>Admin Reports</button>
                  <button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={() => navigate('/higher-authority/authority-reports')}>Authority Reports</button>
                </div>
              </div>
            )}

            {/* Active Deadlines */}
            {assigned.filter(c => c.resolution_deadline && c.status !== 'resolved').length > 0 && (
              <SectionCard title="⏱ Active Resolution Deadlines" subtitle="Complaints assigned to you — live countdown" delay="0.42s">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {assigned
                    .filter(c => c.resolution_deadline && c.status !== 'resolved')
                    .sort((a, b) => new Date(a.resolution_deadline) - new Date(b.resolution_deadline))
                    .slice(0, 8)
                    .map(c => (
                      <div key={c.id}
                        className="ur-row-hover"
                        onClick={() => navigate(`/authority/resolve/${c.id}`)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                          background: c.is_overdue ? 'rgba(239,68,68,0.08)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${c.is_overdue ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.1)'}`,
                        }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.title}
                          </div>
                          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
                            {c.student_name} · {c.department || c.university_category || '—'}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0, marginLeft: 16 }}>
                          <CountdownTimer deadline={c.resolution_deadline} resolvedAt={c.resolved_at} />
                        </div>
                      </div>
                    ))}
                </div>
              </SectionCard>
            )}

            {/* Performance section */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {/* Admin performance */}
              <SectionCard title="Admin Performance" subtitle="Complaint verification activity" delay="0.45s"
                action={<button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 11, padding: '4px 10px' }} onClick={() => navigate('/higher-authority/admins')}>Manage</button>}>
                {!perf.admins.length
                  ? <EmptyState text="No admins yet" sub="Create admins to see performance" />
                  : perf.admins.map(a => <AdminPerfCard key={a.id} admin={a} maxVerified={maxVerified} s={s} />)
                }
              </SectionCard>

              {/* Authority performance */}
              <SectionCard title="Authority Performance" subtitle="Complaint resolution activity" delay="0.5s"
                action={<button className="ur-btn-hover" style={{ ...s.btnSecondary, fontSize: 11, padding: '4px 10px' }} onClick={() => navigate('/higher-authority/authorities')}>Manage</button>}>
                {!perf.authorities.length
                  ? <EmptyState text="No authority members yet" sub="Create authority members to see performance" />
                  : perf.authorities.map(a => <AuthPerfCard key={a.id} auth={a} maxResolved={maxResolved} s={s} />)
                }
              </SectionCard>
            </div>

            {/* Quick Actions */}
            <SectionCard title="Quick Actions" delay="0.55s">
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/higher-authority/create-admin')}>+ Create Admin</button>
                <button className="ur-btn-hover" style={s.btnPrimary} onClick={() => navigate('/higher-authority/create-authority')}>+ Create Authority</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/higher-authority/admins')}>Manage Admins</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/higher-authority/authorities')}>Manage Authorities</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/higher-authority/admin-reports')}>Admin Reports</button>
                <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate('/higher-authority/authority-reports')}>Authority Reports</button>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HigherAuthorityDashboard() {
  return <ThemeProvider><HigherAuthorityDashboardInner /></ThemeProvider>;
}
